import type { TrayDraft } from "@/modules/drafts/draftsRegistry";
import { sectionTitleForLocation } from "@/lib/documentTitle/documentTitle";
import {
  DEFAULT_WORKSPACE_TAB_PREFERENCES,
  type WorkspaceTabPreferences,
} from "@/modules/workspace/workspaceTabPreferences";

/** Enough to work across modules; older tabs close first. Drafts still live in the tray. */
export const MAX_WORKSPACE_TABS = DEFAULT_WORKSPACE_TAB_PREFERENCES.maxTabs;

export type WorkspaceTab = {
  id: string;
  pathname: string;
  search: string;
  title: string;
  /** Kept open. A tab that is not kept is the preview: the next record replaces it. */
  pinned?: boolean;
  /** Pages this tab was on before, oldest first: Back stays inside the tab. */
  history?: WorkspaceHistoryEntry[];
  /** When you last left this tab (ms): news after this shows on the tab. */
  leftAt?: number;
};

export type WorkspaceHistoryEntry = WorkspaceLocation & { title: string };

const MAX_TAB_HISTORY = 20;

export type WorkspaceLocation = {
  pathname: string;
  search: string;
};

export type WorkspaceSession = {
  tabs: WorkspaceTab[];
  activeId: string;
  splitId: string | null;
};

export const workspaceLocationKey = (location: WorkspaceLocation) =>
  `${location.pathname}${location.search}`;

export const moduleSegment = (pathname: string) =>
  pathname.split("/").filter(Boolean)[0] ?? "";

const CREATE_SEGMENTS = new Set(["create", "new"]);

/**
 * Identity of an open record: `kind:id`, or `kind:new` while creating one.
 * Lists and section pages return null. Invoices and subscriptions open in
 * Billing by query (`?invoice=`, `?subscription=`), conversations by
 * `?conversation=`.
 */
export const workspaceRecordKey = (
  location: WorkspaceLocation | string,
): string | null => {
  const { pathname, search } =
    typeof location === "string"
      ? { pathname: location, search: "" }
      : location;
  const params = new URLSearchParams(search);
  const [root, id, extra] = pathname.split("/").filter(Boolean);
  if (!root) return null;
  if (root === "billing") {
    if (id === "invoices" && extra) {
      // A new invoice that autosaved is that draft.
      const draft = params.get("draft");
      if (CREATE_SEGMENTS.has(extra))
        return draft ? `invoices:${draft}` : "invoices:new";
      return `invoices:${extra}`;
    }
    if (params.get("invoice")) return `invoices:${params.get("invoice")}`;
    if (params.get("subscription")) {
      return `subscriptions:${params.get("subscription")}`;
    }
    return null;
  }
  if (root === "messages" && params.get("conversation")) {
    return `conversations:${params.get("conversation")}`;
  }
  if (!id) return null;
  if (
    ["tickets", "deals", "companies", "contacts", "proposals"].includes(root) &&
    id !== "inbox" &&
    id !== "find-duplicates"
  ) {
    return CREATE_SEGMENTS.has(id) ? `${root}:new` : `${root}:${id}`;
  }
  return null;
};

const recordKind = (key: string) => key.slice(0, key.indexOf(":"));

/** Saving a new record moves you to it: still the same piece of work. */
const isSameWork = (from: string | null, to: string | null) =>
  from === to ||
  Boolean(
    from && to && from.endsWith(":new") && recordKind(from) === recordKind(to),
  );

/** Another ticket, invoice, deal, account, or contact — keep the one you are on. */
export const opensAnotherRecord = (
  current: WorkspaceLocation,
  destination: WorkspaceLocation,
) => {
  const from = workspaceRecordKey(current);
  const to = workspaceRecordKey(destination);
  return Boolean(from && to && !isSameWork(from, to));
};

export const fallbackTabTitle = (location: WorkspaceLocation) =>
  sectionTitleForLocation(location.pathname, location.search) ?? "Page";

export const createWorkspaceTab = (
  location: WorkspaceLocation,
  id: string = globalThis.crypto?.randomUUID?.() ?? `tab-${Date.now()}`,
  pinned = false,
): WorkspaceTab => ({
  id,
  pathname: location.pathname,
  search: location.search,
  title: fallbackTabTitle(location),
  ...(pinned ? { pinned: true } : {}),
});

export const tabMatchesDraft = (tab: WorkspaceTab, draft: TrayDraft) => {
  if (draft.kind === "ticket-reply") {
    return (
      tab.pathname === `/tickets/${draft.ticketId}` ||
      tab.pathname.startsWith(`/tickets/${draft.ticketId}/`)
    );
  }
  if (draft.kind === "invoice") {
    return workspaceRecordKey(tab) === `invoices:${draft.invoiceId}`;
  }
  if (!tab.pathname.startsWith("/messages")) return false;
  return (
    new URLSearchParams(tab.search).get("conversation") === draft.conversationId
  );
};

/** Drops the oldest tabs past the limit: previews first, then kept tabs. */
const evictToLimit = (
  tabs: WorkspaceTab[],
  keepId: string,
  protect: Set<string>,
  maxTabs: number,
) => {
  const next = [...tabs];
  const evicted: WorkspaceTab[] = [];
  const removable = (tab: WorkspaceTab) =>
    tab.id !== keepId && !protect.has(tab.id);
  while (next.length > Math.max(1, maxTabs)) {
    const preview = next.findIndex((tab) => removable(tab) && !tab.pinned);
    const unprotected = next.findIndex(removable);
    const fallback = next.findIndex((tab) => tab.id !== keepId);
    const removeAt =
      preview >= 0 ? preview : unprotected >= 0 ? unprotected : fallback;
    if (removeAt < 0) break;
    evicted.push(...next.splice(removeAt, 1));
  }
  return { tabs: next, evicted };
};

/** Open a tab (or, with `reuse`, focus the one on this address) and drop the oldest. */
export const openWorkspaceTab = (
  tabs: WorkspaceTab[],
  destination: WorkspaceLocation,
  options?: {
    protectIds?: string[];
    id?: string;
    pinned?: boolean;
    maxTabs?: number;
    /** Focus a tab already on this address instead of opening another. */
    reuse?: boolean;
    /** Place the new tab right after this one (default: last). */
    after?: string;
  },
): { tabs: WorkspaceTab[]; activeId: string; evicted: WorkspaceTab[] } => {
  const existing = options?.reuse
    ? tabs.find(
        (tab) =>
          workspaceLocationKey(tab) === workspaceLocationKey(destination),
      )
    : undefined;
  if (existing) {
    return {
      tabs: options?.pinned ? pinWorkspaceTab(tabs, existing.id) : tabs,
      activeId: existing.id,
      evicted: [],
    };
  }

  const created = createWorkspaceTab(destination, options?.id, options?.pinned);
  const protect = new Set(options?.protectIds ?? []);
  const afterIndex = options?.after
    ? tabs.findIndex((tab) => tab.id === options.after)
    : -1;
  const placed =
    afterIndex >= 0
      ? [
          ...tabs.slice(0, afterIndex + 1),
          created,
          ...tabs.slice(afterIndex + 1),
        ]
      : [...tabs, created];
  const limited = evictToLimit(
    placed,
    created.id,
    protect,
    options?.maxTabs ?? MAX_WORKSPACE_TABS,
  );
  return { ...limited, activeId: created.id };
};

/** Keep a tab open so the next record does not replace it. */
export const pinWorkspaceTab = (tabs: WorkspaceTab[], id: string) =>
  tabs.some((tab) => tab.id === id && !tab.pinned)
    ? tabs.map((tab) => (tab.id === id ? { ...tab, pinned: true } : tab))
    : tabs;

type ClosedWorkspaceTabs = {
  tabs: WorkspaceTab[];
  activeId: string;
  splitId: string | null;
  /** The tab to open next when the current one closed. */
  activate: WorkspaceTab | null;
  closed: WorkspaceTab[];
};

/** Close several tabs at once; one tab always stays open. */
export const closeWorkspaceTabs = (
  tabs: WorkspaceTab[],
  activeId: string,
  closeIds: string[],
  splitId: string | null,
): ClosedWorkspaceTabs => {
  const closing = new Set(closeIds);
  const closed = tabs.filter((tab) => closing.has(tab.id));
  const next = tabs.filter((tab) => !closing.has(tab.id));
  if (!closed.length || !next.length) {
    return { tabs, activeId, splitId, activate: null, closed: [] };
  }
  const index = tabs.findIndex((tab) => tab.id === activeId);
  const activate = closing.has(activeId)
    ? (tabs.slice(index + 1).find((tab) => !closing.has(tab.id)) ??
      next[next.length - 1] ??
      null)
    : null;
  return {
    tabs: next,
    activeId: activate?.id ?? activeId,
    splitId: splitId && closing.has(splitId) ? null : splitId,
    activate,
    closed,
  };
};

export const closeWorkspaceTab = (
  tabs: WorkspaceTab[],
  activeId: string,
  closeId: string,
  splitId: string | null,
) => closeWorkspaceTabs(tabs, activeId, [closeId], splitId);

/** Keep the current tab's address in step with the router, unless another tab already owns it. */
/**
 * Point a tab at a new address. Same record: keep its title and whether it is
 * kept. Another record: that work is gone from this tab, so it is a preview.
 */
const moveTab = (
  tabs: WorkspaceTab[],
  id: string,
  here: WorkspaceLocation,
  /** False for redirects and address fix-ups that Back should skip. */
  remember = true,
): WorkspaceTab[] =>
  tabs.map((tab) => {
    if (tab.id !== id) return tab;
    const from = workspaceRecordKey(tab);
    const to = workspaceRecordKey(here);
    const sameRecord =
      from || to ? isSameWork(from, to) : tab.pathname === here.pathname;
    const { pinned, history = [], ...rest } = tab;
    const nextHistory = remember
      ? [
          ...history,
          { pathname: tab.pathname, search: tab.search, title: tab.title },
        ].slice(-MAX_TAB_HISTORY)
      : history;
    return {
      ...rest,
      pathname: here.pathname,
      search: here.search,
      title: sameRecord && from === to ? tab.title : fallbackTabTitle(here),
      ...(pinned && (sameRecord || (!from && !to)) ? { pinned } : {}),
      ...(nextHistory.length ? { history: nextHistory } : {}),
    };
  });

/**
 * The browser went Back (or Forward). Stay in the current tab: return to a
 * page it was on before, never to a page another tab owns.
 * `target` is where the address must go when it differs from `here`.
 */
export const backInWorkspaceTab = (
  session: WorkspaceSession,
  here: WorkspaceLocation,
): { session: WorkspaceSession; target: WorkspaceLocation | null } => {
  const active = session.tabs.find((tab) => tab.id === session.activeId);
  if (!active || workspaceLocationKey(active) === workspaceLocationKey(here)) {
    return { session, target: null };
  }
  const history = active.history ?? [];
  const hereKey = workspaceLocationKey(here);
  let index = -1;
  for (let i = history.length - 1; i >= 0; i--) {
    if (workspaceLocationKey(history[i]) === hereKey) {
      index = i;
      break;
    }
  }
  if (index < 0 && !history.length) {
    // Nothing earlier in this tab: stay where you are.
    return {
      session,
      target: { pathname: active.pathname, search: active.search },
    };
  }
  const at = index >= 0 ? index : history.length - 1;
  const entry = history[at];
  const tabs = session.tabs.map((tab) => {
    if (tab.id !== active.id) return tab;
    const { history: _previous, ...rest } = tab;
    const remaining = history.slice(0, at);
    return {
      ...rest,
      pathname: entry.pathname,
      search: entry.search,
      title: entry.title,
      ...(remaining.length ? { history: remaining } : {}),
    };
  });
  return {
    session: { ...session, tabs },
    target:
      workspaceLocationKey(entry) === hereKey
        ? null
        : { pathname: entry.pathname, search: entry.search },
  };
};

export const followWorkspaceLocation = (
  tabs: WorkspaceTab[],
  activeId: string,
  location: WorkspaceLocation,
  remember = true,
): WorkspaceTab[] => {
  const active = tabs.find((tab) => tab.id === activeId);
  if (!active) return tabs;
  if (workspaceLocationKey(active) === workspaceLocationKey(location))
    return tabs;
  // Another tab may show the same page: you still navigate here.
  return moveTab(tabs, activeId, location, remember);
};

/** After a reload: the tab you were on takes the address in the bar. */
export const reconcileWorkspaceTabs = (
  stored: WorkspaceSession | null,
  here: WorkspaceLocation,
): WorkspaceSession => {
  if (!stored?.tabs.length) {
    const tab = createWorkspaceTab(here);
    return { tabs: [tab], activeId: tab.id, splitId: null };
  }
  const splitId = stored.tabs.some((tab) => tab.id === stored.splitId)
    ? stored.splitId
    : null;
  const active =
    stored.tabs.find((tab) => tab.id === stored.activeId) ?? stored.tabs[0];
  if (workspaceLocationKey(active) === workspaceLocationKey(here)) {
    return { tabs: stored.tabs, activeId: active.id, splitId };
  }
  return {
    tabs: moveTab(stored.tabs, active.id, here, false),
    activeId: active.id,
    splitId,
  };
};

export type NavigationIntent = "passthrough" | "open-tab";

export const navigationIntent = ({
  modifier,
  destination,
  current,
  dirty,
  opening = "new-tab",
}: {
  modifier: boolean;
  destination: WorkspaceLocation | null;
  current: WorkspaceLocation;
  dirty: boolean;
  opening?: WorkspaceTabPreferences["opening"];
}): NavigationIntent => {
  // Tabs off: Cmd/Ctrl-click falls through to a browser tab.
  if (!destination || opening === "off") return "passthrough";
  if (workspaceLocationKey(destination) === workspaceLocationKey(current)) {
    return "passthrough";
  }
  if (modifier) return "open-tab";
  // Preview: the route moves first and adoptWorkspaceLocation picks the tab.
  if (opening === "preview") return "passthrough";
  if (opensAnotherRecord(current, destination)) return "open-tab";
  // Lists and other modules open here; drafts stay in the tray.
  void dirty;
  return "passthrough";
};

/** Work on a record is kept: opening another record must not replace it. */
const leavesKeptTab = (active: WorkspaceLocation, here: WorkspaceLocation) => {
  const from = workspaceRecordKey(active);
  const to = workspaceRecordKey(here);
  return Boolean(from && to && !isSameWork(from, to));
};

/**
 * Apply the address the router already moved to.
 *
 * - `preview`: the preview tab follows you from record to record. A kept tab
 *   stays put; leaving it moves you to the preview tab (or a new one).
 * - `new-tab`: a different record becomes its own tab.
 * - `off`: one tab follows the address.
 * A filter change on the same record stays on the current tab.
 */
export const adoptWorkspaceLocation = (
  session: WorkspaceSession,
  here: WorkspaceLocation,
  preferences: WorkspaceTabPreferences = {
    ...DEFAULT_WORKSPACE_TAB_PREFERENCES,
    opening: "new-tab",
  },
  /** False when the address was replaced (redirect, autosave): Back skips it. */
  remember = true,
): WorkspaceSession & { evicted?: WorkspaceTab[] } => {
  const active = session.tabs.find((tab) => tab.id === session.activeId);
  const { opening, maxTabs } = preferences;
  const follow = () => {
    const tabs = followWorkspaceLocation(
      session.tabs,
      session.activeId,
      here,
      remember,
    );
    return tabs === session.tabs ? session : { ...session, tabs };
  };
  if (!active) return session;
  // You navigate in the tab you are on, even when another tab shows the
  // same page or record. Tabs change only when you choose one.
  if (workspaceLocationKey(active) === workspaceLocationKey(here))
    return session;
  if (opening === "off") return follow();

  // Everything else (lists, other modules) happens in this tab; Back
  // returns. Only another record opened from one you are working on gets
  // its own tab, right next to this one.
  const divert =
    opening === "preview"
      ? Boolean(active.pinned) && leavesKeptTab(active, here)
      : opensAnotherRecord(active, here);
  if (!divert) return follow();

  const opened = openWorkspaceTab(session.tabs, here, {
    maxTabs,
    after: active.id,
    protectIds: [session.activeId, session.splitId].filter((id): id is string =>
      Boolean(id),
    ),
  });
  return {
    tabs: opened.tabs,
    activeId: opened.activeId,
    splitId: opened.tabs.some((tab) => tab.id === session.splitId)
      ? session.splitId
      : null,
    evicted: opened.evicted,
  };
};

/**
 * Typing or choosing in a form is work on this tab. Search and filter boxes,
 * the top bar, and fields marked `data-workspace-tab-ignore` are not.
 */
export const isWorkOnPage = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;
  if (target.closest("header, [role='tablist'], [data-workspace-tab-ignore]")) {
    return false;
  }
  if (target instanceof HTMLInputElement) {
    if (["search", "checkbox", "radio"].includes(target.type)) return false;
    const label = `${target.placeholder} ${target.getAttribute("aria-label") ?? ""}`;
    if (/\b(search|filter)/i.test(label)) return false;
    return true;
  }
  if (
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return true;
  }
  return target instanceof HTMLElement && target.isContentEditable;
};

export type WorkspaceTabKind =
  | "ticket"
  | "invoice"
  | "subscription"
  | "proposal"
  | "billing"
  | "meeting"
  | "calendar"
  | "account"
  | "project"
  | "lead"
  | "task"
  | "mail"
  | "message"
  | "marketing"
  | "report"
  | "settings"
  | "dashboard"
  | "page";

const KIND_BY_SEGMENT: Record<string, WorkspaceTabKind> = {
  tickets: "ticket",
  proposals: "proposal",
  meetings: "meeting",
  calendar: "calendar",
  accounts: "account",
  clients: "account",
  contacts: "account",
  companies: "account",
  leads: "lead",
  deals: "project",
  projects: "project",
  tasks: "task",
  mail: "mail",
  messages: "message",
  marketing: "marketing",
  reports: "report",
  settings: "settings",
  profile: "settings",
};

/** What a tab shows, for its icon. */
export const workspaceTabKind = (
  location: WorkspaceLocation,
): WorkspaceTabKind => {
  const record = workspaceRecordKey(location);
  if (record?.startsWith("invoices:")) return "invoice";
  if (record?.startsWith("subscriptions:")) return "subscription";
  const segment = moduleSegment(location.pathname);
  if (!segment) return "dashboard";
  if (segment === "billing") {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab === "subscriptions") return "subscription";
    if (tab === "proposals") return "proposal";
    return "billing";
  }
  return KIND_BY_SEGMENT[segment] ?? "page";
};

/** The record id a tab shows, when it is a ticket or an invoice. */
export const workspaceTabRecord = (
  location: WorkspaceLocation,
): { kind: "ticket" | "invoice"; id: string } | null => {
  const record = workspaceRecordKey(location);
  if (!record || record.endsWith(":new")) return null;
  const [kind, id] = [
    recordKind(record),
    record.slice(record.indexOf(":") + 1),
  ];
  if (kind === "tickets") return { kind: "ticket", id };
  if (kind === "invoices") return { kind: "invoice", id };
  return null;
};

/** Drag a tab to another tab's place. */
export const moveWorkspaceTabTo = (
  tabs: WorkspaceTab[],
  fromId: string,
  toId: string,
): WorkspaceTab[] => {
  const from = tabs.findIndex((tab) => tab.id === fromId);
  const to = tabs.findIndex((tab) => tab.id === toId);
  if (from < 0 || to < 0 || from === to) return tabs;
  const next = [...tabs];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/** Mark the tab you leave, so news that arrives after shows on it. */
export const markWorkspaceTabLeft = (
  tabs: WorkspaceTab[],
  leftId: string,
  at = Date.now(),
): WorkspaceTab[] =>
  tabs.map((tab) => (tab.id === leftId ? { ...tab, leftAt: at } : tab));

export const internalPathFromAnchor = (
  anchor: HTMLAnchorElement,
  origin: string,
): WorkspaceLocation | null => {
  if (anchor.target === "_blank" || anchor.hasAttribute("download"))
    return null;
  const href = anchor.getAttribute("href");
  if (
    !href ||
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:")
  ) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(anchor.href, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  return { pathname: url.pathname, search: url.search };
};
